import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDIDDetail, useDIDDelegates } from '../api/hooks';
import { apiFetch } from '../api/client';
import { encodePathSegment } from '../lib/url';
import { useWeb3 } from '../contexts/Web3Context';
import { useNetwork } from '../contexts/NetworkContext';
import { describeTxError } from '../lib/txError';
import { useDIDRegistry } from '../hooks/useDIDRegistry';
import { txUrl, shortHash } from '../lib/chainExplorer';

type Tab = 'overview' | 'keys' | 'delegates' | 'services';

interface TxBanner {
  txHash: string;
  blockNumber: number;
  label: string;
}

export default function DIDDetail() {
  const { did } = useParams<{ did: string }>();
  const decodedDid = did ? decodeURIComponent(did) : '';
  const { data: resolution, loading, error, refetch } = useDIDDetail(decodedDid);
  const { data: delegates, refetch: refetchDelegates } = useDIDDelegates(decodedDid);
  const [tab, setTab] = useState<Tab>('overview');
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation(['dids', 'common']);

  const [delegateForm, setDelegateForm] = useState({ delegateType: 'veriKey', delegate: '', validity: '86400' });
  const [delegateError, setDelegateError] = useState<string | null>(null);
  const [addingDelegate, setAddingDelegate] = useState(false);
  const [copiedController, setCopiedController] = useState<string | null>(null);

  const [showServiceForm, setShowServiceForm] = useState(false);
  const [serviceForm, setServiceForm] = useState({ id: '', type: '', serviceEndpoint: '' });
  const [addingService, setAddingService] = useState(false);
  const [serviceError, setServiceError] = useState<string | null>(null);

  const [txBanner, setTxBanner] = useState<TxBanner | null>(null);
  const [txError, setTxError] = useState<string | null>(null);

  const { isConnected, connect, provider, account } = useWeb3();
  const { selected } = useNetwork();
  const {
    deactivateDID,
    addDelegate: addDelegateOnChain,
    revokeDelegate: revokeDelegateOnChain,
    pending: txPending,
    isReady: contractReady,
  } = useDIDRegistry();

  const doc = resolution?.didDocument as Record<string, unknown> | undefined ?? resolution;
  const verificationMethods = (doc?.verificationMethod ?? []) as Record<string, unknown>[];
  const services = (doc?.service ?? []) as Record<string, unknown>[];
  const controllers = (doc?.controller ?? []) as string[];

  const copyDID = () => {
    if (decodedDid) {
      navigator.clipboard.writeText(decodedDid);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const isValidDelegate = (addr: string) => addr.startsWith('did:') || /^0x[a-fA-F0-9]{40}$/.test(addr);

  const copyController = (ctrl: string) => {
    navigator.clipboard.writeText(ctrl);
    setCopiedController(ctrl);
    setTimeout(() => setCopiedController(null), 2000);
  };

  const handleTxError = async (e: unknown) => {
    setTxError(await describeTxError(e, { provider, account, chain: selected }));
  };

  const handleAddDelegate = async () => {
    if (!decodedDid || !delegateForm.delegate) return;
    if (!isValidDelegate(delegateForm.delegate)) {
      setDelegateError(t('detail.delegates.invalid'));
      return;
    }
    setDelegateError(null);
    setTxError(null);
    setAddingDelegate(true);
    try {
      if (contractReady) {
        const result = await addDelegateOnChain(
          decodedDid,
          delegateForm.delegateType,
          delegateForm.delegate,
          parseInt(delegateForm.validity),
        );
        setTxBanner({ ...result, label: 'Delegate added on-chain' });
        // Best-effort off-chain sync.
        await apiFetch(`/dids/${encodePathSegment(decodedDid)}/delegates`, {
          method: 'POST',
          body: JSON.stringify({
            delegateType: delegateForm.delegateType,
            delegate: delegateForm.delegate,
            validity: parseInt(delegateForm.validity),
            txHash: result.txHash,
          }),
        }).catch(() => {});
      } else {
        await apiFetch(`/dids/${encodePathSegment(decodedDid)}/delegates`, {
          method: 'POST',
          body: JSON.stringify({
            delegateType: delegateForm.delegateType,
            delegate: delegateForm.delegate,
            validity: parseInt(delegateForm.validity),
          }),
        });
      }
      refetchDelegates();
      setDelegateForm({ ...delegateForm, delegate: '' });
    } catch (e) {
      handleTxError(e);
    } finally {
      setAddingDelegate(false);
    }
  };

  const handleRevokeDelegate = async (delegateType: string, delegate: string) => {
    if (!decodedDid) return;
    setTxError(null);
    try {
      if (contractReady) {
        const result = await revokeDelegateOnChain(decodedDid, delegateType, delegate);
        setTxBanner({ ...result, label: 'Delegate revoked on-chain' });
        await apiFetch(`/dids/${encodePathSegment(decodedDid)}/delegates`, {
          method: 'DELETE',
          body: JSON.stringify({ delegateType, delegate, txHash: result.txHash }),
        }).catch(() => {});
      } else {
        await apiFetch(`/dids/${encodePathSegment(decodedDid)}/delegates`, {
          method: 'DELETE',
          body: JSON.stringify({ delegateType, delegate }),
        });
      }
      refetchDelegates();
    } catch (e) {
      handleTxError(e);
    }
  };

  const handleDeactivate = async () => {
    if (!decodedDid || !confirm(t('detail.deactivateConfirm'))) return;
    setTxError(null);
    try {
      if (contractReady) {
        const result = await deactivateDID(decodedDid);
        setTxBanner({ ...result, label: 'DID deactivated on-chain' });
        await apiFetch(`/dids/${encodePathSegment(decodedDid)}`, {
          method: 'DELETE',
          body: JSON.stringify({ txHash: result.txHash }),
        }).catch(() => {});
      } else {
        await apiFetch(`/dids/${encodePathSegment(decodedDid)}`, { method: 'DELETE' });
      }
      refetch();
    } catch (e) {
      handleTxError(e);
    }
  };

  const tabs: { id: Tab; labelKey: string; icon: string }[] = [
    { id: 'overview', labelKey: 'detail.tabs.overview', icon: 'info' },
    { id: 'keys', labelKey: 'detail.tabs.keys', icon: 'key' },
    { id: 'delegates', labelKey: 'detail.tabs.delegates', icon: 'group' },
    { id: 'services', labelKey: 'detail.tabs.services', icon: 'dns' },
  ];

  const isDeactivated = doc?.deactivated === true;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/my-dids" className="rounded-lg p-1.5 text-text-faint hover:bg-slate-100 hover:text-text-main">
            <span className="material-symbols-outlined" style={{ fontSize: 20 }}>arrow_back</span>
          </Link>
          <div>
            <h1 className="text-xl font-bold text-text-main">{t('detail.title')}</h1>
            <div className="mt-1 flex items-center gap-2">
              <code className="rounded border border-slate-200 bg-slate-50 px-2 py-0.5 font-mono text-xs text-text-main">
                {decodedDid}
              </code>
              <button onClick={copyDID} className="rounded p-0.5 text-text-faint hover:text-primary">
                <span className="material-symbols-outlined" style={{ fontSize: 14 }}>
                  {copied ? 'check' : 'content_copy'}
                </span>
              </button>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
            isDeactivated
              ? 'border-red-200 bg-red-50 text-red-700'
              : 'border-emerald-200 bg-emerald-50 text-emerald-700'
          }`}>
            <span className={`size-1.5 rounded-full ${isDeactivated ? 'bg-red-500' : 'bg-emerald-500'}`} />
            {isDeactivated ? t('common:status.deactivated') : t('common:status.active')}
          </span>
          {!isDeactivated && (
            <button
              onClick={handleDeactivate}
              disabled={txPending}
              className="flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-bold text-red-600 hover:bg-red-50 disabled:opacity-60"
            >
              {txPending ? (
                <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600" />
              ) : contractReady ? (
                <span className="material-symbols-outlined" style={{ fontSize: 14 }}>account_balance_wallet</span>
              ) : null}
              {t('detail.deactivate')}
            </button>
          )}
        </div>
      </div>

      {/* Wallet connect nudge (only on write tabs) */}
      {!isConnected && (tab === 'delegates' || tab === 'services') && (
        <div className="flex items-center justify-between rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <p className="text-xs text-amber-800">
            <span className="font-bold">Connect MetaMask</span> to write operations on-chain.
          </p>
          <button onClick={connect}
            className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-amber-700">
            Connect
          </button>
        </div>
      )}

      {/* Tx confirmation banner */}
      {txBanner && (
        <div className="flex items-center justify-between rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
          <div className="flex items-center gap-2 text-sm text-emerald-700">
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>check_circle</span>
            <span>{txBanner.label} — block {txBanner.blockNumber}</span>
          </div>
          {txUrl(txBanner.txHash) ? (
            <a href={txUrl(txBanner.txHash)!} target="_blank" rel="noopener noreferrer"
              className="text-xs font-bold text-emerald-700 underline">
              {shortHash(txBanner.txHash)} ↗
            </a>
          ) : (
            <span className="font-mono text-xs text-emerald-600">{shortHash(txBanner.txHash)}</span>
          )}
          <button onClick={() => setTxBanner(null)} className="ml-2 text-emerald-500 hover:text-emerald-700">
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>close</span>
          </button>
        </div>
      )}

      {/* Tx error banner */}
      {txError && (
        <div className="flex items-center justify-between rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <div className="flex items-center gap-2 text-sm text-red-700">
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>error</span>
            <span>{txError}</span>
          </div>
          <button onClick={() => setTxError(null)} className="text-red-400 hover:text-red-600">
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>close</span>
          </button>
        </div>
      )}

      <div className="flex gap-1 border-b border-border-subtle">
        {tabs.map(tb => (
          <button
            key={tb.id}
            onClick={() => setTab(tb.id)}
            className={`flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-bold transition-colors ${
              tab === tb.id
                ? 'border-primary text-primary'
                : 'border-transparent text-text-faint hover:text-text-main'
            }`}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{tb.icon}</span>
            {t(tb.labelKey)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
      ) : error ? (
        <div className="text-center py-20 text-red-500">{t('common:errors.loadDidFailed', { error })}</div>
      ) : (
        <>
          {tab === 'overview' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-xl border border-border-subtle bg-white p-4">
                  <p className="text-xs font-bold text-text-faint">{t('common:labels.created')}</p>
                  <p className="mt-1 text-sm font-bold text-text-main">{doc?.created ? new Date(doc.created as string).toLocaleString() : 'N/A'}</p>
                </div>
                <div className="rounded-xl border border-border-subtle bg-white p-4">
                  <p className="text-xs font-bold text-text-faint">{t('common:labels.updated')}</p>
                  <p className="mt-1 text-sm font-bold text-text-main">{doc?.updated ? new Date(doc.updated as string).toLocaleString() : 'N/A'}</p>
                </div>
                <div className="rounded-xl border border-border-subtle bg-white p-4">
                  <p className="text-xs font-bold text-text-faint">{t('common:labels.controllers')}</p>
                  {controllers.length > 0 ? (
                    <div className="mt-1 space-y-1">
                      {controllers.map((ctrl) => (
                        <div key={ctrl} className="flex items-center gap-1.5">
                          <code className="text-sm font-bold text-text-main truncate">{ctrl}</code>
                          <button onClick={() => copyController(ctrl)} className="rounded p-0.5 text-text-faint hover:text-primary">
                            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>
                              {copiedController === ctrl ? 'check' : 'content_copy'}
                            </span>
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-1 text-sm font-bold text-text-main">{t('common:labels.self')}</p>
                  )}
                </div>
                <div className="rounded-xl border border-border-subtle bg-white p-4">
                  <p className="text-xs font-bold text-text-faint">{t('common:labels.verificationMethods')}</p>
                  <p className="mt-1 text-sm font-bold text-text-main">{verificationMethods.length}</p>
                </div>
              </div>
              <div className="rounded-xl border border-border-subtle bg-white p-4">
                <p className="mb-2 text-xs font-bold text-text-faint">{t('common:labels.didDocument')}</p>
                <pre className="max-h-96 overflow-auto rounded-lg bg-slate-50 p-4 font-mono text-xs text-text-main">
                  {JSON.stringify(doc, null, 2)}
                </pre>
              </div>
            </div>
          )}

          {tab === 'keys' && (
            <div className="space-y-4">
              {verificationMethods.length === 0 ? (
                <p className="py-10 text-center text-sm text-text-faint">{t('common:empty.noVerificationMethods')}</p>
              ) : (
                verificationMethods.map((vm, i) => (
                  <div key={i} className="rounded-xl border border-border-subtle bg-white p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex size-10 items-center justify-center rounded-lg bg-blue-50">
                          <span className="material-symbols-outlined text-primary" style={{ fontSize: 20 }}>key</span>
                        </div>
                        <div>
                          <p className="text-sm font-bold text-text-main">{String(vm.id ?? '')}</p>
                          <p className="text-xs text-text-faint">{String(vm.type ?? '')}</p>
                        </div>
                      </div>
                      <span className="rounded-full bg-blue-50 px-2.5 py-1 text-[10px] font-bold text-primary">
                        {String(vm.type ?? '').replace('VerificationKey2020', '').replace('VerificationKey2019', '')}
                      </span>
                    </div>
                    {!!vm.publicKeyMultibase && (
                      <div className="mt-3 rounded-lg bg-slate-50 p-3">
                        <p className="text-[10px] font-bold text-text-faint">{t('common:labels.publicKeyMultibase')}</p>
                        <code className="mt-1 block break-all font-mono text-xs text-text-main">{String(vm.publicKeyMultibase)}</code>
                      </div>
                    )}
                    <p className="mt-2 text-xs text-text-faint">{t('common:labels.controller', { controller: String(vm.controller ?? '') })}</p>
                  </div>
                ))
              )}
            </div>
          )}

          {tab === 'delegates' && (
            <div className="space-y-4">
              {!isDeactivated && (
                <div className="rounded-xl border border-border-subtle bg-white p-4">
                  <p className="mb-3 text-sm font-bold text-text-main">{t('detail.delegates.addTitle')}</p>
                  <div className="flex gap-3">
                    <select
                      value={delegateForm.delegateType}
                      onChange={e => setDelegateForm({ ...delegateForm, delegateType: e.target.value })}
                      className="rounded-lg border border-border-subtle px-3 py-2 text-sm"
                    >
                      <option value="veriKey">{t('detail.delegates.veriKey')}</option>
                      <option value="sigAuth">{t('detail.delegates.sigAuth')}</option>
                    </select>
                    <input
                      type="text"
                      value={delegateForm.delegate}
                      onChange={e => setDelegateForm({ ...delegateForm, delegate: e.target.value })}
                      placeholder={t('detail.delegates.delegatePlaceholder')}
                      className="flex-1 rounded-lg border border-border-subtle px-3 py-2 text-sm"
                    />
                    <select
                      value={delegateForm.validity}
                      onChange={e => setDelegateForm({ ...delegateForm, validity: e.target.value })}
                      className="rounded-lg border border-border-subtle px-3 py-2 text-sm"
                    >
                      <option value="3600">{t('detail.delegates.validity.hour')}</option>
                      <option value="86400">{t('detail.delegates.validity.day')}</option>
                      <option value="604800">{t('detail.delegates.validity.week')}</option>
                      <option value="2592000">{t('detail.delegates.validity.month')}</option>
                      <option value="31536000">{t('detail.delegates.validity.year')}</option>
                    </select>
                    <button
                      onClick={handleAddDelegate}
                      disabled={addingDelegate || !delegateForm.delegate || txPending}
                      className="btn-primary flex items-center gap-1.5 disabled:opacity-60"
                    >
                      {(addingDelegate || txPending) ? (
                        <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                      ) : contractReady ? (
                        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>account_balance_wallet</span>
                      ) : null}
                      {addingDelegate ? t('detail.delegates.adding') : t('detail.delegates.add')}
                    </button>
                  </div>
                  {delegateError && <p className="mt-2 text-xs text-red-600">{delegateError}</p>}
                </div>
              )}

              {(!delegates || delegates.length === 0) ? (
                <div className="py-10 text-center">
                  <span className="material-symbols-outlined mb-2 text-text-faint" style={{ fontSize: 36 }}>group</span>
                  <p className="text-sm text-text-faint">{t('common:empty.noActiveDelegates')}</p>
                  <p className="text-xs text-text-faint mt-1">{t('common:empty.addDelegatesHint')}</p>
                </div>
              ) : (
                delegates.map(d => (
                  <div key={d.id} className="flex items-center justify-between rounded-xl border border-border-subtle bg-white p-4">
                    <div className="flex items-center gap-3">
                      <div className="flex size-10 items-center justify-center rounded-lg bg-purple-50">
                        <span className="material-symbols-outlined text-purple-600" style={{ fontSize: 20 }}>
                          {d.delegateType === 'sigAuth' ? 'shield' : 'key'}
                        </span>
                      </div>
                      <div>
                        <code className="text-sm font-bold text-text-main">{d.delegate}</code>
                        <div className="flex items-center gap-3 mt-1 text-xs text-text-faint">
                          <span className="rounded bg-purple-50 px-1.5 py-0.5 text-[10px] font-bold text-purple-700">{d.delegateType}</span>
                          <span>{t('common:labels.expires', { date: new Date(d.validTo).toLocaleDateString() })}</span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRevokeDelegate(d.delegateType, d.delegate)}
                      disabled={txPending}
                      className="flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-bold text-red-600 hover:bg-red-50 disabled:opacity-60"
                    >
                      {contractReady && !txPending && (
                        <span className="material-symbols-outlined" style={{ fontSize: 12 }}>account_balance_wallet</span>
                      )}
                      {t('detail.delegates.revoke')}
                    </button>
                  </div>
                ))
              )}
            </div>
          )}

          {tab === 'services' && (
            <div className="space-y-4">
              {!isDeactivated && (
                <>
                  {!showServiceForm ? (
                    <button onClick={() => setShowServiceForm(true)} className="btn-primary w-full justify-center">
                      <span className="material-symbols-outlined" style={{ fontSize: 18 }}>add_circle</span>
                      {t('detail.services.addEndpoint')}
                    </button>
                  ) : (
                    <div className="rounded-xl border border-border-subtle bg-slate-50 p-4 space-y-3">
                      <h4 className="text-sm font-bold text-text-main">{t('detail.services.newEndpoint')}</h4>
                      <input
                        type="text" placeholder={t('detail.services.idPlaceholder')} value={serviceForm.id}
                        onChange={e => setServiceForm({ ...serviceForm, id: e.target.value })}
                        className="w-full rounded-lg border border-border-subtle bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                      />
                      <input
                        type="text" placeholder={t('detail.services.typePlaceholder')} value={serviceForm.type}
                        onChange={e => setServiceForm({ ...serviceForm, type: e.target.value })}
                        className="w-full rounded-lg border border-border-subtle bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                      />
                      <input
                        type="text" placeholder={t('detail.services.endpointPlaceholder')} value={serviceForm.serviceEndpoint}
                        onChange={e => setServiceForm({ ...serviceForm, serviceEndpoint: e.target.value })}
                        className="w-full rounded-lg border border-border-subtle bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                      />
                      {serviceError && <p className="text-xs text-red-500">{serviceError}</p>}
                      <div className="flex gap-2">
                        <button
                          disabled={addingService}
                          onClick={async () => {
                            if (!serviceForm.id || !serviceForm.type || !serviceForm.serviceEndpoint) {
                              setServiceError(t('detail.services.errorRequired')); return;
                            }
                            setAddingService(true); setServiceError(null);
                            try {
                              const updatedServices = [...services.map(s => ({ id: String(s.id ?? ''), type: String(s.type ?? ''), serviceEndpoint: String(s.serviceEndpoint ?? '') })), serviceForm];
                              await apiFetch(`/dids/${encodePathSegment(decodedDid)}`, { method: 'PUT', body: JSON.stringify({ services: updatedServices }) });
                              setServiceForm({ id: '', type: '', serviceEndpoint: '' }); setShowServiceForm(false); refetch();
                            } catch (e: unknown) {
                              setServiceError(e instanceof Error ? e.message : String(e));
                            } finally { setAddingService(false); }
                          }}
                          className="btn-primary"
                        >
                          {addingService ? t('detail.services.adding') : t('detail.services.add')}
                        </button>
                        <button onClick={() => { setShowServiceForm(false); setServiceError(null); }}
                          className="rounded-lg border border-border-subtle px-4 py-2 text-sm font-bold text-text-faint hover:bg-slate-50">
                          {t('common:actions.cancel')}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
              {services.length === 0 ? (
                <p className="py-10 text-center text-sm text-text-faint">{t('common:empty.noServiceEndpoints')}</p>
              ) : (
                services.map((svc, i) => (
                  <div key={i} className="rounded-xl border border-border-subtle bg-white p-4">
                    <div className="flex items-center gap-3">
                      <div className="flex size-10 items-center justify-center rounded-lg bg-emerald-50">
                        <span className="material-symbols-outlined text-emerald-600" style={{ fontSize: 20 }}>dns</span>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-text-main">{String(svc.id ?? '')}</p>
                        <p className="text-xs text-text-faint">{t('common:labels.type', { type: String(svc.type ?? '') })}</p>
                        <p className="text-xs text-primary mt-0.5">{String(svc.serviceEndpoint ?? '')}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
